import * as _ from "lodash";
import * as React from "react";
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";
import {DataView} from "@bizform/primereact/components/dataview/DataView";
import {Dropdown} from "@bizform/primereact/components/dropdown/Dropdown";
import {Checkbox} from "@bizform/primereact/components/checkbox/Checkbox";
import {OverlayPanel} from "@bizform/primereact/components/overlaypanel/OverlayPanel";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {DefaultLayoutId} from "@bizform/type/model/component_element/DefaultLayoutId.model";
import {PluginType} from "@bizform/type/model/model/Plugin.model";
import {DomAttribute} from "@bizform/type/model/dom/DomAttribute.model";
import {
    DoGridAction,
    DoRetrieve,
    GridAutoSaveKeyConstant,
    GridClientPagingKeyConstant,
    GridClientPagingSizeKeyConstant,
    GridIdKeyConstant,
    GridLinkIdKeyConstant,
    GridRetrieveKeyConstant,
    GridValidationKeyConstant,
} from "@bizform/type/model/component_element/manifest_constant/Grid.constant";
import {WindowKey} from "@bizform/type/model/model/WindowKey.model";
import {GridSortOrder} from "@bizform/type/model/data_set/LookUp.model";
import {GridSubject, GridSubjectType, NotificationSubjectType} from "@bizform/type/model/model/Subject.model";
import {SortOption} from "@bizform/type/model/rest_api/_BaseRestApi.model";
import {GridFilters} from "@bizform/type/model/rest_api/Grid.model";
import {
    GridContainerRole,
    GridContextMenu,
    GridRepeatedFiltersInformation,
    GridTableColumnsInformation
} from "@bizform/type/model/form/Grid.model";
import {
    ComponentHelper,
    Debug,
    DomHelper,
    FunctionHelper,
    StringHelper
} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    BlockHandler,
    ComponentElementClass,
    FormDataSchemaService,
    FormValidationService,
    GridActionService,
    GridDataService,
    GridSchemaService,
    ModalHandler,
    ReactComponentElementInit,
    SubjectService
} from "@bizform/core/dist/js/bizflow/core.min";
import {GridLayoutRow} from "../bf_layout/layout/BfGridLayout";
import {AbstractComponentElement} from "../bf_component/AbstractComponentElement";
import {GridHelper} from "./helper/Grid.helper";
import {Filter} from "./helper/Filter";
import {ContextHelper} from "./helper/Context.helper";


type Props =  {
};

type State = BaseStateModel & {
    [GridIdKeyConstant]?: string;
    [GridAutoSaveKeyConstant]?: boolean;
    [GridValidationKeyConstant]?: boolean;
    [GridClientPagingKeyConstant]?: boolean;
    [GridClientPagingSizeKeyConstant]?: boolean;
    [GridValidationKeyConstant]?: boolean;
    autoLoad?: boolean;
    gridModel?: GridLayoutRow;
    layout?: string;
    reload?: boolean;
    sort?: boolean;
    sortOrder?: string;
    defaultSort?: string;
    sortField?: Array<string>;
    filter?: boolean;
    overlay?: boolean;
    filterField?: Array<GridRepeatedFiltersInformation>;
    columns?: Array<GridTableColumnsInformation>;
    click?: string;
    state?: string;
    context?: Array<GridContextMenu>;
    header?: boolean;
    footer?: boolean;
    autoResize?: boolean;
    pageNo?: number;
    pageLinkSize?: number;
    paginator?: boolean;
    rows?: number;
    totalRecords?: number;
    paginatorPosition?: string;
    alwaysShowPaginator?: boolean;
    rowsPerPageOptions?: number[];
    currentPageReportTemplate?: string;
    emptyMessage?: string;
    containerClassName?: string;
    containerStyle?: object;
    sortClassName?: string;
    sortStyle?: object;
    filterClassName?: string;
    filterStyle?: object;
    headerClassName?: string;
    headerStyle?: object;
    itemClassName?: string;
    itemStyle?: object;
    footerClassName?: string;
    footerStyle?: object;
    onPage?(e: {originalEvent: Event, first: number, rows: number}): void;
    itemTemplate?(item: any, layout: "grid" | "list"): JSX.Element | undefined;
};

/**
 * Grid Action Container
 *
 * @since 0.0.1
 * @author Heungsoo Ham
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfGridRepeatContainer extends AbstractComponentElement<Props, State> {
    static readonly pluginType: string = PluginType.GRIDHANDLER;

    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_g5ukxQgUsHUcgwBSodXMwUiaRmcQVyK",
        category: ComponentCategory.HANDLER,
        defaultLayoutId: DefaultLayoutId.ONLYCOMPONENTELEMENT,
        label: "Repeated Container",
        description: "Repeated Container",
        icon: "",
        property: null
    };

    setProperty(property: ComponentElementProperty) {
        try {
            FormValidationService.regValidationHandler((this as any).___props);
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this._clientPagingSize = GridHelper.setClientPaging(newState);
            if (newState[GridAutoSaveKeyConstant]) {
                GridActionService.regAutoSave(this.applicationId, this.formId, newState[GridIdKeyConstant], newState[GridLinkIdKeyConstant], this._elementId);
            }
            if (this._forcedFilter === false) {
                this._forcedFilter = true;
            }
            this._filterField = null;
            if (newState.filter) {
                this._filterStructure(newState);
            }
            GridSchemaService.setRetrieve(this._elementId, newState[GridRetrieveKeyConstant]);
            GridActionService.regRepeatedContainer((this as any).___props, property.attribute, this._contextAndClick);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    emitHasContainerKey(): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfGridRepeatContainer)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
        this._elementId = StringHelper.elementId(this.elementId);
        if (!this._gridSubject && !window[WindowKey.DESIGNMODE] && !window[WindowKey.VERSIONMODE]) {
            this._gridSubject = SubjectService.gridSubject.pipe(
                filter((data: GridSubject) =>
                    this.state &&
                    (
                        (data.type === GridSubjectType.RELOAD && data.containerElementId === this._elementId) ||
                        (data.formId === this.formId && data[GridIdKeyConstant] === this.state[GridIdKeyConstant] && (
                            (data.type === GridSubjectType.CALLACTION && ["Run" + DoGridAction.ADD, "Run" + DoGridAction.RELOAD, DoRetrieve.RELOADRETRIEVE].indexOf(data.value) > -1) ||
                            (data.type === GridSubjectType.REQRELOAD && this.elementId !== data.containerElementId))
                        )
                    )
                )
            ).subscribe(
                (data: GridSubject) => {
                    if (this.displayMode) {
                        if (data.type === GridSubjectType.RELOAD) {
                            if (!data.value.page) {
                                this._onReload();
                                return;
                            }
                            if (data.value.forced) {
                                this._pageNo = null;
                            }
                            this.setPage(data.value.page);
                            return;
                        }
                        const retrieve = this.state[GridRetrieveKeyConstant] === true;
                        if (data.type === GridSubjectType.REQRELOAD && data.uniqueId) {
                            if (data.uniqueId === this._searchId) {
                                return;
                            }
                        }
                        if (data.type === GridSubjectType.CALLACTION && data[GridLinkIdKeyConstant] === this.state[GridLinkIdKeyConstant]) {
                            if (data.containerRole !== GridContainerRole.SEARCH && retrieve && ((retrieve && data.value === "Run" + DoGridAction.RELOAD && data.containerElementId !== this._elementId) || (data.value !== DoRetrieve.RELOADRETRIEVE && data.containerElementId !== this._elementId) || (data.value === DoRetrieve.RELOADRETRIEVE && data.containerElementId === this._elementId))) {
                                GridDataService.checkEpk(data.applicationId, data.formId, data[GridIdKeyConstant], data[GridLinkIdKeyConstant], this._elementId, null);
                                return;
                            }
                            if (data.value !== DoRetrieve.RELOADRETRIEVE && data.containerElementId !== this._elementId) {
                                if ((retrieve && !data.retrieve) || (!retrieve && data.retrieve)) {
                                    return;
                                }
                            }
                            if (data.value !== "Run" + DoGridAction.RELOAD || data.containerRole === GridContainerRole.SEARCH || data.containerElementId === this._elementId || (this._hasPk !== true && GridDataService.hasPk(this.formId, data[GridIdKeyConstant], data[GridLinkIdKeyConstant], data.containerElementId, this.state[GridLinkIdKeyConstant], this._elementId, data.value))) {
                                this._pageNo = null;
                                const pageNo = data.value === "Run" + DoGridAction.RELOAD ? this.state.pageNo : 1;
                                const searchId = (data.value === "Run" + DoGridAction.RELOAD && data.uniqueId) ? data.uniqueId : null;
                                this.setPage(pageNo, data.containerRole !== GridContainerRole.SEARCH, false, searchId).then();
                            }
                        } else if (data.containerRole !== GridContainerRole.SEARCH && (data.value === "Run" + DoGridAction.RELOAD || data.value === "Run" + DoGridAction.ADD || data.type === GridSubjectType.REQRELOAD)) {
                            if (data.containerElementId !== this._elementId && !retrieve) {
                                if (this._hasPk !== true && GridDataService.hasPk(this.formId, data[GridIdKeyConstant], data[GridLinkIdKeyConstant], data.containerElementId, this.state[GridLinkIdKeyConstant], this._elementId, data.value)) {
                                    this._hasPk = true;
                                    this._onReload(true);
                                }
                            }
                        }
                    }
                }
            );
        }
    }

    destroy(): void {
        FormValidationService.removeValidationHandler((this as any).___props);
        this._list = null;
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _pageNo: number;
    private _idx: number;
    private _listSize: number;
    private readonly _fakeDom: Array<HTMLElement>;
    private _directDom: HTMLElement;
    private _list: Array<string>;
    private _filter: any;
    private _forcedFilter: boolean;
    private _filterField: Array<GridRepeatedFiltersInformation>;
    private _elementId: string;
    private _hasPk: boolean;
    private _gridSubject: Subscription;
    private _filters: Array<GridFilters>;
    private _regResize: boolean;
    private _ref: HTMLElement;
    private _offsetHeight: number;
    private _searchId: string;
    private _context: any;
    private _contextContainer: HTMLElement;
    private _clientPagingSize?: number;
    private readonly _contextList: {
        [key: string]: Array<HTMLElement>;
    };

    constructor(props?: Props) {
        super(props);
        this.state = {};
        this._fakeDom = [];
        this._contextList = {};

        this._header = this._header.bind(this);
        this._footer = this._footer.bind(this);
        this._sort = this._sort.bind(this);
        this._itemTemplate = this._itemTemplate.bind(this);
        this._itemTemplateD = this._itemTemplateD.bind(this);
        this._setGridModel = this._setGridModel.bind(this);
        this._onSortChange = this._onSortChange.bind(this);
        this._onSortOrderChange = this._onSortOrderChange.bind(this);
        this._onReload = this._onReload.bind(this);
        this._onFilter = this._onFilter.bind(this);
        this._callbackList = this._callbackList.bind(this);
        this._setViewRef = this._setViewRef.bind(this);
        this._setFilterRef = this._setFilterRef.bind(this);
        this._setRef = this._setRef.bind(this);
        this._setContextRef = this._setContextRef.bind(this);
        this._autoResize = this._autoResize.bind(this);
        this.setPage = this.setPage.bind(this);
        this.onPage = this.onPage.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this._overlay = this._overlay.bind(this);
        this._contextAndClick = this._contextAndClick.bind(this);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (this.state[GridIdKeyConstant] && !this.state.pageNo) {
            this.state.pageNo = 1;
            if (this.state[GridRetrieveKeyConstant] !== true && this.state.autoLoad !== false) {
                this.setPage(1, true).then();
            } else {
                this._pageNo = 1;
            }
        }
    }

    componentWillUnmount() {
        if (this._regResize) {
            ModalHandler.removeOpenCallback(this._autoResize);
        }
        ModalHandler.removeOpenCallback(this._overlay);
        ModalHandler.removeCloseCallback(this._overlay);
        GridActionService.unRegRepeatedContainer((this as any).___props);
    }

    render(): React.ReactNode {
        if (!this.displayMode || !this.state[GridIdKeyConstant]) {
            return null;
        } else {
            this._idx = -1;
            const className = "m-0 p-0 w-100" + (this.state.containerClassName ? " " + this.state.containerClassName : "");
            const first = this.state.paginator && this.state.pageNo ? (this.state.pageNo - 1) * this.state.rows : 0;
            let value = null;
            const paginator = (this.state.paginator && !this.state.alwaysShowPaginator && (!this.state.totalRecords || this.state.totalRecords <= this.state.rows)) ? false : this.state.paginator;
            if (window[WindowKey.DESIGNMODE] || window[WindowKey.VERSIONMODE]) {
                value = [{index: 0}];
            } else if (paginator) {
                value = this._listSize ? [{}] : null;
            } else if (this._listSize) {
                value = [];
                for (let i = 0; i < this._listSize; i++) {
                    value.push({index: i});
                }
            }
            const attr = this.state.columns && this.state.columns.length ? {
                [DomAttribute.GRIDID]: this.state[GridIdKeyConstant],
                [DomAttribute.GRIDSEARCHCONTAINER]: this._elementId,
                [DomAttribute.GRIDLINKID]: this.state[GridLinkIdKeyConstant]
            } : null;
            const page = paginator ? {
                paginator: paginator,
                paginatorPosition: this.state.paginatorPosition,
                alwaysShowPaginator: this.state.alwaysShowPaginator,
                pageLinkSize: this.state.pageLinkSize,
            } : null;
            const view = <DataView
                {...page}
                ref={this._setViewRef}
                key={this.elementId}
                id={this.elementId}
                value={value}
                header={this.state.header || this.state.sort || this.state.filter ? this._header() : null}
                footer={this.state.footer ? this._footer() : null}
                emptyMessage={this.state.emptyMessage}
                layout={this.state.layout}
                rows={this.state.rows}
                first={first}
                totalRecords={this.state.totalRecords}
                onPage={this.onPage}
                itemTemplate={window[WindowKey.DESIGNMODE] || window[WindowKey.VERSIONMODE] ? this._itemTemplateD : this._itemTemplate}
                className={className}
                style={this.state.containerStyle as any}
            ></DataView>;
            return (
                <React.Fragment>
                    {GridHelper.infoGridContainer(this.parentDom, this.state[GridIdKeyConstant], "Repeated Container", this.elementId,  this.state[GridLinkIdKeyConstant], this.elementId, "Repeat")}
                    {this.state.autoResize ? <div ref={this._setRef} className="m-0 p-0">{view}</div> : view}
                    <OverlayPanel
                        key={this._elementId + "_context"}
                        ref={this._setContextRef}
                        showCloseIcon={false}
                        dismissable={true}
                        appendTo={DomHelper.getForOverLayDom()}
                    >
                        <div id={this.elementId + "_context"} className={"m-0 p-0"} {...attr}></div>
                    </OverlayPanel>
                </React.Fragment>
            );
        }
    }

    onSearch(filters?: Array<GridFilters>) {
        if (this._filter && this._filter.hide) {
            this._filter.hide(null);
        }
        this._filters = filters;
        this.setPage(1, null, null, null, true).then();
    }

    private _header(): JSX.Element | string {
        const width = this.state.sort && (this.state.reload || this.state.filter) ? 400 : (this.state.sort ? 350 : 50);
        const sortClassName = (this.state.sortClassName ? this.state.sortClassName + " " : "") + "float-right p-inputgroup";
        const sortStyle = _.merge({"maxWidth": "100%", "minWidth": width + "px"}, this.state.sortStyle);
        const sort = this.state.sort || this.state.reload || this.state.filter;
        const filter = this.state.filter && this._filterField && this._filterField.length ? this._makeFilter() : null;
        if (!this.state.header && !sort) {
            return null;
        } else if (!this.state.header && sort) {
            return (
                <React.Fragment>
                    <div className={this.state.headerClassName} style={this.state.headerStyle}>
                        <div className={sortClassName} style={sortStyle}>{this._sort()}</div>
                        <div className="clearfix"></div>
                        {filter}
                    </div>
                </React.Fragment>
            );
        } else {
            const h = GridHelper.indicatorGridContainer("Header", true);
            const attr = {
                [DomAttribute.GRIDID]: this.state[GridIdKeyConstant],
                [DomAttribute.CONTAINER]: "header",
                [DomAttribute.GRIDSEARCHCONTAINER]: this._elementId,
                [DomAttribute.GRIDLINKID]: this.state[GridLinkIdKeyConstant],
                [DomAttribute.GRIDROOT]: this.elementId + "-direct",
            };
            if (this.state.header && !sort) {
                return (
                    <React.Fragment>
                        {filter}
                        {h}
                        <div className={this.state.headerClassName} style={this.state.headerStyle} {...attr}></div>
                    </React.Fragment>
                );
            } else {
                return (
                    <React.Fragment>
                        <div className={sortClassName} style={sortStyle}>{this._sort()}</div>
                        <div className="clearfix"></div>
                        {filter}
                        {h}
                        <div className={this.state.headerClassName} style={this.state.headerStyle} {...attr}></div>
                    </React.Fragment>
                );
            }
        }
    }

    private _footer(): JSX.Element | string {
        const f = GridHelper.indicatorGridContainer("Footer", true);
        const attr = {
            [DomAttribute.GRIDID]: this.state[GridIdKeyConstant],
            [DomAttribute.CONTAINER]: "footer",
            [DomAttribute.GRIDSEARCHCONTAINER]: this._elementId,
            [DomAttribute.GRIDROOT]: this.elementId + "-direct",
            [DomAttribute.GRIDLINKID]: this.state[GridLinkIdKeyConstant],
        };
        return (
            <React.Fragment>
                {f}
                <div className={this.state.footerClassName} style={this.state.footerStyle} { ...attr }></div>
            </React.Fragment>
        );
    }

    private _makeFilter(): React.ReactNode {
        const attr = {
            applicationId: this.applicationId,
            formId: this.formId,
            [GridIdKeyConstant]: this.state[GridIdKeyConstant],
            [GridLinkIdKeyConstant]: this.state[GridLinkIdKeyConstant],
            containerElementId: StringHelper.elementId(this.elementId),
            forcedRerender: this._forcedFilter,
            filter: this._filterField
        };
        this._forcedFilter = false;
        const f = <Filter key={this.elementId + "_filter"} {...attr} onSearch={this.onSearch}/>;
        if (this.state.overlay) {
            const filterClassName = this.state.filter && this.state.filterClassName ? this.state.filterClassName : null;
            return <OverlayPanel key={this.elementId + "_overlay"} ref={this._setFilterRef} showCloseIcon={true} dismissable={false} appendTo={DomHelper.getForOverLayDom()} className={"bf-bg-theme bf-text-theme"}>
                <div style={{minWidth: "300px"}}>
                    <div className={filterClassName} style={this.state.filterStyle}>{f}</div>
                </div>
            </OverlayPanel>;
        } else {
            const filterClassName = (this.state.filter && this.state.filterClassName ? this.state.filterClassName + " " : "") + "d-none";
            return <div ref={this._setFilterRef} className={filterClassName} style={this.state.filterStyle}>{f}</div>;
        }
    }

    private _filterStructure(newState: State) {
        if (!Array.isArray(this._filterField)) {
            this._filterField = [];
            for (const i in newState.filterField) {
                if (newState.filterField[i] && newState.filterField[i].entityId && newState.filterField[i].use && newState.filterField[i].options && newState.filterField[i].options.length) {
                    this._filterField.push({
                        entityId: newState.filterField[i].entityId,
                        type: newState.filterField[i].type,
                        label: newState.filterField[i].label,
                        filterFunc: newState.filterField[i].filterFunc,
                        placeholder: newState.filterField[i].placeholder,
                        format: newState.filterField[i].format,
                        options: newState.filterField[i].options
                    });
                }
            }
            return this._filterField;
        }
        return this._filterField;
    }

    private _sort(): React.ReactNode {
        let className;
        if (this.state.sort && (this.state.reload || this.state.filter)) {
            className = ["w-50 p-inputgroup-addon", "w-25 p-inputgroup-addon pt-2"];
        } else if (this.state.sort) {
            className = ["w-75 p-inputgroup-addon", "w-25 p-inputgroup-addon pt-2"];
        } else {
            className = ["", "w-100 p-inputgroup-addon"];
        }
        const filter = this.state.filter && this._filterField && this._filterField.length ? (
            <button key={this.elementId + "_filter"} onClick={this._onFilter} className="p-button p-component p-button-primary p-button-icon-only" style={{width: "28px", height: "28px"}} title="Filter">
                <span className="fas fa-search p-c p-button-icon-left"></span>
            </button>
        ) : null;
        const reload = this.state.reload ? (
            <div className={className[1]}>
                {filter}
                <button key={this.elementId + "_reload"} onClick={this._onReload} className="p-button p-component p-button-secondary p-button-icon-only" style={{width: "28px", height: "28px"}} title="Reload">
                    <span className="fas fa-sync-alt p-c p-button-icon-left"></span>
                </button>
            </div>
        ) : this.state.filter ? <div className={className[1]}>{filter}</div> : null;
        const sort = this.state.sort ? (
            <React.Fragment>
                <div className={className[0]}>
                    <Dropdown key={this.elementId + "_sort"} options={this._setSortField()} value={this.state.defaultSort} placeholder="Sort By" showClear={true} onChange={this._onSortChange}/>
                </div>
                <div className={className[1]}>
                    <Checkbox key={this.elementId + "_order"} id={this.elementId + "_order"} value={GridSortOrder.DESC} checked={this.state.sortOrder === GridSortOrder.DESC} onChange={this._onSortOrderChange}></Checkbox>
                    <label htmlFor={this.elementId + "_order"} className="p-checkbox-label">DESC</label>
                </div>
            </React.Fragment>
        ) : null;
        return (
            <React.Fragment>
                {sort}
                {reload}
            </React.Fragment>
        );
    }

    private _itemTemplate(fake: any, layout: "grid" | "list"): JSX.Element | undefined {
        const item = this._getValue();
        if (item) {
            const attr = {
                [DomAttribute.GRIDID]: this.state[GridIdKeyConstant],
                [DomAttribute.GRIDLINKID]: this.state[GridLinkIdKeyConstant],
                [DomAttribute.GRIDDIRECTPARENT]: item.index,
                [DomAttribute.GRIDROOT]: this.elementId + "-direct"
            };
            const className = (this.state.itemClassName ? this.state.itemClassName + " " : "") + (this._setGridModel(layout));
            return (
                <div {...attr} className={className} style={this.state.itemStyle}></div>
            );
        } else {
            return null;
        }
    }

    private _itemTemplateD(item: any, layout: "grid" | "list"): JSX.Element | undefined {
        if (item) {
            const i = GridHelper.indicatorGridContainer("Item", true);
            const attr = {
                [DomAttribute.GRIDID]: this.state[GridIdKeyConstant],
                [DomAttribute.GRIDLINKID]: this.state[GridLinkIdKeyConstant],
                [DomAttribute.CONTAINER]: item.index,
                [DomAttribute.GRIDDIRECTCONTAINER]: this._elementId,
                [DomAttribute.GRIDPK]: this._elementId
            };
            return (
                <div className={this._setGridModel(layout)}>
                    {i}
                    <div {...attr} className={this.state.itemClassName} style={this.state.itemStyle}></div>
                </div>
            );
        } else {
            return null;
        }
    }

    private _setGridModel(layout: "grid" | "list"): string {
        if (layout === "list") {
            return "p-col-12";
        }
        const col = this.state.gridModel.cols.items[0];
        const xs = col.xs ? " p-col-" + col.xs : "";
        const sm = col.sm ? " p-sm-" + col.sm : "";
        const md = col.md ? " p-md-" + col.md : "";
        const lg = col.lg ? " p-lg-" + col.lg : "";
        const xl = col.xl ? " p-xl-" + col.xl : "";
        const value = `${xs}${sm}${md}${lg}${xl}`.trim();
        return value ? value : "p-col-12";
    }

    private _setSortField(): any {
        const sf = this.state.sortField;
        const v = [];
        if (sf && sf.length) {
            for (const key in sf) {
                const s = FormDataSchemaService.getProjectDataSchemaFieldData(this.applicationId, sf[key], true);
                if (s) {
                    v.push({
                        label: s.label || s.name,
                        value: sf[key]
                    });
                }
            }
        }
        return v;
    }

    private _onSortChange(e: {originalEvent: Event, value: any}) {
        this.setState({
            ...this.state,
            defaultSort: e.value
        }, this._onReload);
    }

    private _onSortOrderChange(e: {originalEvent: Event, value: any}) {
        this.setState({
            ...this.state,
            sortOrder: (e as any).checked ? GridSortOrder.DESC : GridSortOrder.ASC
        }, this._onReload);
    }

    private _onReload(init?: any) {
        const forced = init && init.target ? true : false;
        this.setPage(this._pageNo, init === true ? true : false, true, null, forced).then();
    }

    private _onFilter(e?: any) {
        if (this._filter) {
            if (this._filter && this._filter.toggle) {
                this._filter.container.style.width = this.parentDom.offsetWidth + "px";
                this._filter.toggle(e);
            } else {
                this._filter.classList[this._filter.classList.contains("d-none") ? "remove" : "add"]("d-none");
            }
        }
    }

    private async _unload(): Promise<void> {
        this._makeFakeDom();
        await FunctionHelper.timeout(300);
        BlockHandler.unload(this.elementId);
        if (this._hasPk) {
            await FunctionHelper.timeout(300);
            this._hasPk = false;
        }
    }

    private _getValue(): any {
        this._idx++;
        if (this._idx < this._listSize) {
            return {
                index: this._idx
            };
        } else {
            return null;
        }
    }

    private _callbackList() {
        let info = GridDataService.getGridInfoData(this.formId, this.state[GridIdKeyConstant], this.state[GridLinkIdKeyConstant], this._elementId);
        FormValidationService.regGridFirstData((this as any).___props, info);
        let list = GridDataService.getGridListData(this.formId, this.state[GridIdKeyConstant], this.state[GridLinkIdKeyConstant], this._elementId);
        this._list = [];
        if (list && Object.keys(list)) {
            let i = 0;
            Reflect.ownKeys(list).forEach(
                (k: string) => {
                    if (i < this.state.rows) {
                        this._list.push(k);
                    }
                    i++;
                }
            );
        }
        this._pageNo = info && info.pageNo ? info.pageNo : 1;
        this._listSize = this._list.length;
        this._removeFakeDom();
        this.setState({
            pageNo: this._pageNo,
            totalRecords: info && info.totalCount ? info.totalCount : 0
        }, this._unload);
    }

    private _makeSort(): Array<SortOption> {
        if (this.state.defaultSort) {
            return [{
                field: this.state.defaultSort,
                order: this.state.sortOrder as any
            }];
        }
        return null;
    }

    private _makeFakeDom() {
        let s = this._fakeDom.length;
        let e = s;
        for (let i = 0; i < this._listSize; i++) {
            if (!this._fakeDom[i]) {
                const fake = document.createElement("div");
                fake.setAttribute(DomAttribute.GRIDDIRECTCONTAINER, this._elementId);
                fake.setAttribute(DomAttribute.CONTAINER, String(i));
                this._fakeDom[i] = fake;
                e++;
            }
            const pDom = this.parentDom.querySelector(`[${DomAttribute.GRIDDIRECTPARENT}="${i}"]`);
            pDom.setAttribute(DomAttribute.GRIDPK, this._list[i]);
            pDom.appendChild(this._fakeDom[i]);
            if (!this._directDom) {
                this._directDom = pDom.parentElement;
                this._directDom.setAttribute("id", this.elementId + "-direct");
            }
        }
        SubjectService.notificationSubject.next({
            applicationId: this.applicationId,
            formId: (this as any).___props.formId,
            elementId: this._elementId,
            type: NotificationSubjectType.GRIDREPEATCONTAINERRENDERING,
            value: {
                start: s,
                end: e,
                dom: this.parentDom.querySelector(".p-dataview-content>.p-grid")
            }
        });
    }

    private _removeFakeDom() {
        if (this._fakeDom.length) {
            for (let i = 0; i < this._fakeDom.length; i++) {
                if (this._fakeDom[i].parentElement) {
                    this._fakeDom[i].parentElement.removeChild(this._fakeDom[i]);
                } else {
                    break;
                }
            }
        }
    }

    private _setViewRef(ref: any) {
        if (ref) {
            ModalHandler.regOpenCallback(this._overlay);
            this.readyComponent();
        }
    }

    private _setFilterRef(ref: any) {
        if (ref) {
            this._filter = ref;
        }
    }

    private _setRef(ref: any) {
        if (ref) {
            this._ref = ref;
            if (!this._regResize) {
                if (DomHelper.modalLinkParentDom(this.parentDom)) {
                    ModalHandler.regOpenCallback(this._autoResize);
                } else {
                    this._autoResize(this.parentDom.parentElement, true);
                }
                this._ref.parentElement.style.position = "relative";
                this._regResize = true;
            }
            if (DomHelper.modalLinkParentDom(this.parentDom)) {
                ModalHandler.regCloseCallback(this._overlay);
            }
        }
    }

    private _autoResize(dom: HTMLElement, forced?: boolean) {
        const modal = DomHelper.getModalContentDomFromDom(this.parentDom);
        if (dom && (forced || (modal && dom === modal.querySelector(`[${DomAttribute.MODALBODY}]`)))) {
            const ht = dom.offsetHeight;
            if (ht && ht !== this._offsetHeight) {
                dom.style.overflowY = "hidden";
                this._offsetHeight = ht;
                const c = (this._ref.querySelector(".p-dataview-content") as HTMLElement);
                if (c) {
                    const h = (this._ref.querySelector(".p-dataview-header") as HTMLElement);
                    const p = (this._ref.querySelector(".p-paginator") as HTMLElement);
                    const f = (this._ref.querySelector(".p-dataview-footer") as HTMLElement);
                    let ah = (h ? h.clientHeight : 0) + (p ? p.clientHeight : 0) + (f ? f.clientHeight : 0);
                    c.style.height = (ht - ah - 10) + "px";
                    c.style.overflowY = "auto";
                }
            }
        }
    }

    private _overlay() {
        if (this._filter && this._filter.hide) {
            this._filter.hide(null);
        }
        if (this._context && this._context.hide) {
            this._context.hide(null);
        }
    }

    private _removeContextList() {
        for (const k in this._contextList) {
            for (const l in this._contextList[k]) {
                DomHelper.purge(this._contextList[k][l]);
                this._contextList[k][l] = null;
            }
        }
    }

    private _contextAndClick(e: any, entityId: string) {
        const index = _.findIndex(this.state.columns, {entityId: entityId});
        if (index > -1) {
            const mode = e.type === "click" ? e.type : "context";
            ContextHelper.onAction(e.target, e, mode, this.applicationId, this.formId, this.state.columns, index, this._context, this._contextList, this.state, this._contextContainer, this._overlay, (this as any).___props);
        }
    }

    private _setContextRef(e: any) {
        if (e) {
            this._context = e;
            this._contextContainer = e.container.querySelector("#" + this.elementId + "_context");
            this._contextContainer.parentElement.classList.add("pt-1");
            this._contextContainer.parentElement.classList.add("pr-1");
            this._contextContainer.parentElement.classList.add("pb-0");
            this._contextContainer.parentElement.classList.add("pl-1");
            this._removeContextList();
        }
    }

    // Inner Link
    async setPage(pageNo: number, init?: boolean, reload?: boolean, searchId?: string, forced?: boolean): Promise<void> {
        this._searchId = searchId;
        if (!window[WindowKey.DESIGNMODE] && !window[WindowKey.VERSIONMODE] && pageNo && (forced || filter || reload || this._pageNo !== pageNo)) {
            BlockHandler.load(this.elementId, this.parentDom);
            await FunctionHelper.timeout();
            const sort = this._makeSort();
            let func = () => {
                if (func) {
                    this._overlay();
                    GridDataService.getList(this.applicationId, this.formId, this.state[GridIdKeyConstant], this.state[GridLinkIdKeyConstant], this._elementId, sort, this._filters, pageNo === -1 ? 1 : pageNo, this.state.rows, init, this._callbackList, searchId, forced ? null : this._clientPagingSize, pageNo === -1);
                    if (abort) {
                        (abort as any).__proto__ = null;
                        abort = null;
                    }
                    (func as any).__proto__ = null;
                    func = null;
                }
            };
            let abort = () => {
                BlockHandler.unload(this.elementId);
                if (func) {
                    (func as any).__proto__ = null;
                    func = null;
                }
                (abort as any).__proto__ = null;
                (abort as any).__proto__ = null;
            };
            if (!init && this.state[GridAutoSaveKeyConstant] && pageNo !== -1) {
                GridActionService.runUpdate(this.applicationId, this.formId, this.state[GridIdKeyConstant], this.state[GridLinkIdKeyConstant], this._elementId, func, abort, this._directDom, false, this.state[GridValidationKeyConstant]);
            } else {
                func();
            }
        }
    }

    // Outer Link
    onPage(e: {originalEvent: Event, first: number, rows: number}) {
        const page = (e.originalEvent as any).page + 1;
        if (page !== this._pageNo) {
            this.setPage(page).then();
        }
    }
}
